import React, { useState, useEffect, useMemo, useRef } from "react";
import classnames from "classnames";
import lottie, { AnimationItem } from "lottie-web/build/player/lottie_light";
import { i18n, faceRecognition } from "@/utils";
import animationData from "@/assets/detecting.json";
import Icon from "../icon";
import styles from "./style.less";
import { faceDetect } from "@sunzi/utils";
import { base64ToBlob, thirdServiceUsed } from "@sunzi/utils";
import { IFaceDetectResponse } from "@sunzi/utils/types/api/face-detect";

/** 检测任务状态 */
enum TaskStatus {
  Detecting, // 检测中
  Failed, // 检测失败
  Done, // 检测完成
}

interface BlurTipProps {
  /** 识别图片 */
  source: string | Blob | File;
  /** 关闭回调 */
  onClose: () => void;
  /** 替换图片回调 */
  onReplace: () => void;
  /** 谷歌分析 */
  gtag: any;
}

const BlurTip: React.FC<BlurTipProps> = ({
  source,
  onClose,
  onReplace,
  gtag,
}) => {
  /** 动画挂载 dom */
  const _animation = useRef<any>(null);
  /** 动画实例 */
  const lottieAnimation = useRef<AnimationItem>();
  /** 任务状态 */
  const [taskStatus, setTaskStatus] = useState<TaskStatus>(
    TaskStatus.Detecting,
  );
  /** 创建图片 url */
  const createImageUrl = useMemo(() => {
    const type = Object.prototype.toString.call(source);
    if (type === "[object String]") {
      return source as string;
    } else {
      const blobUrl = URL.createObjectURL(source as Blob | File);
      return blobUrl;
    }
  }, []);

  /** 初始化动画 */
  const animationInit = () => {
    if (_animation.current) {
      lottieAnimation.current = lottie.loadAnimation({
        container: _animation.current,
        renderer: "svg",
        loop: true,
        autoplay: true,
        animationData: animationData,
      });
    }
  };

  const isBlur = (face: IFaceDetectResponse) => {
    const { blurness, gaussianblur, motionblur } = face.attributes.blur;
    // eslint-disable-next-line @typescript-eslint/no-shadow
    let isBlur;
    if (
      blurness.value === motionblur.value &&
      gaussianblur.value === motionblur.value
    ) {
      // 模糊数据一致
      if (blurness.value > blurness.threshold) {
        isBlur = true;
      } else {
        isBlur = false;
      }
    } else if (
      blurness.value > blurness.threshold ||
      motionblur.value > motionblur.threshold ||
      gaussianblur.value > gaussianblur.threshold
    ) {
      isBlur = true;
    } else if (
      blurness.value < blurness.threshold ||
      motionblur.value < motionblur.threshold ||
      gaussianblur.value < gaussianblur.threshold
    ) {
      isBlur = false;
    }

    return isBlur;
  };

  /** 识别人像是否模糊 */
  const identifyBlur = async (image: string | Blob) => {
    try {
      const response = await faceDetect(
        typeof image === "string" ? base64ToBlob(image) : image,
      );
      if (isBlur(response[0])) {
        gtag("event", "event_is_blur");
        setTaskStatus(TaskStatus.Done);
      } else {
        gtag("event", "event_no_blur");
        onClose();
      }
    } catch (error) {
      setTaskStatus(TaskStatus.Failed);
    } finally {
      thirdServiceUsed({
        type: 5,
        source: 4,
        internalSource: 3,
      });
    }
  };

  /** 重新请求人脸识别 */
  const handleRetry = () => {
    setTaskStatus(TaskStatus.Detecting);
  };

  useEffect(() => {
    if (taskStatus === TaskStatus.Detecting) {
      animationInit();
      identifyBlur(source);
    }
  }, [source, taskStatus]);

  return (
    <div
      className={classnames(styles.blurTip, {
        [styles.lowPreview]: taskStatus === TaskStatus.Done,
      })}
    >
      {taskStatus === TaskStatus.Detecting && (
        <div className={styles.detect}>
          <div ref={_animation} className={styles.detectAnimation} />
          <div className={styles.detectText}>
            {i18n.format("components.blur.tip.detect.text")}
          </div>
          <div className={styles.detectSkip} onClick={onClose}>
            {i18n.format("components.blur.tip.detect.skip")}
          </div>
        </div>
      )}
      {taskStatus === TaskStatus.Failed && (
        <div className={styles.failed}>
          <div className={styles.failedIcon}>
            <Icon type="photo-error" color="#ff533a" size={30} />
          </div>
          <div className={styles.failedText}>
            {i18n.format("components.blur.tip.failed.text")}
          </div>
          <div className={styles.failedButton}>
            <div className={styles.failedButtonRetry} onClick={handleRetry}>
              {i18n.format("components.blur.tip.failed.retry")}
            </div>
            <div className={styles.failedButtonClose} onClick={onClose}>
              {i18n.format("components.blur.tip.failed.close")}
            </div>
          </div>
        </div>
      )}
      {taskStatus === TaskStatus.Done && (
        <div className={styles.lowQuality}>
          <div className={styles.closeIcon} onClick={onClose}>
            <Icon type="close-circle" color="#d3d3d3" size={20} />
          </div>
          <div className={styles.imageWrapper}>
            <img src={createImageUrl} />
          </div>
          <div className={styles.lowQualityTitle}>
            {i18n.format("components.blur.tip.low.quality.title")}
          </div>
          <div className={styles.lowQualityDetail}>
            {i18n.format("components.blur.tip.low.quality.detail")}
          </div>
          <div className={styles.replaceButtonWrapper}>
            <div className={styles.replaceButton} onClick={onReplace}>
              <Icon type="camera" color="#fff" size={20} />
              <span>{i18n.format("modules.global.replace")}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlurTip;
